import { useCallback, useEffect, useState } from 'react';
import {
  fetchAiChanges,
  markAiReviewed,
  type AiChangeEntry,
} from '@shared/utils';
import { Button } from './Button';

interface AiReviewSectionProps {
  /** audit_logs の entityType（'quote' | 'customer' 等） */
  entityType: string;
  /** Firestore コレクション名（'quotes' | 'customers' 等） */
  collectionName: string;
  entityId: string;
  /** 対象レコードの aiPendingReview。falsy なら何も描画しない。 */
  pending: boolean | undefined;
  /** ログイン中ユーザー（useAuth().user?.id）。レビュー確定の実行者として記録する。 */
  userId: string | undefined;
  /** フィールド名→日本語ラベル。無いフィールドはフィールド名のまま表示。 */
  fieldLabels?: Record<string, string>;
  /** レビュー確定が完了したら呼ばれる（親がバッジ非表示・再取得を行う）。 */
  onConfirmed?: () => void;
}

/** Firestore Timestamp らしき値なら Date へ（違えば null）。 */
function toDate(value: unknown): Date | null {
  if (
    typeof value === 'object' &&
    value !== null &&
    'toDate' in value &&
    typeof (value as { toDate: unknown }).toDate === 'function'
  ) {
    return (value as { toDate: () => Date }).toDate();
  }
  return null;
}

/** 差分の値を人が読める短い文字列へ（日付は YYYY-MM-DD、オブジェクトはJSON、空は「(未設定)」）。 */
function formatValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return '(未設定)';
  const date = toDate(value);
  if (date) {
    const pad = (n: number) => `${n}`.padStart(2, '0');
    const ymd = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
    // 時刻が 00:00 の値（有効期限など日付として入力された値）は日付だけ見せる
    return date.getHours() === 0 && date.getMinutes() === 0
      ? ymd
      : `${ymd} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
  }
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

/**
 * 「🤖 AIレビュー待ち」パネル。AIによる変更箇所（audit_logs 由来）を表示し、
 * 人が「レビュー済みにする」を押すと aiPendingReview をフィールドごと削除する。
 * データ駆動のため、pending でないレコードでは一切描画されない（設計書 §3.5）。
 */
export function AiReviewSection({
  entityType,
  collectionName,
  entityId,
  pending,
  userId,
  fieldLabels,
  onConfirmed,
}: AiReviewSectionProps) {
  const [entries, setEntries] = useState<AiChangeEntry[] | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    // 対象が切り替わったとき、前のレコードの履歴を残さない
    setEntries(null);
    setLoadError(false);
    if (!pending) return;
    let cancelled = false;
    fetchAiChanges(entityType, entityId)
      .then((result) => {
        if (!cancelled) setEntries(result);
      })
      .catch(() => {
        // 履歴が読めなくても確定ボタンは使えるようにする（表示だけ諦める）
        if (!cancelled) setLoadError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [pending, entityType, entityId]);

  const confirm = useCallback(async () => {
    if (!userId) return;
    setSaving(true);
    setSaveError(null);
    try {
      await markAiReviewed(collectionName, entityType, entityId, userId);
      onConfirmed?.();
    } catch {
      setSaveError('レビュー確定に失敗しました。時間をおいて再度お試しください。');
    } finally {
      setSaving(false);
    }
  }, [collectionName, entityType, entityId, userId, onConfirmed]);

  if (!pending) return null;

  const label = (field: string) => fieldLabels?.[field] ?? field;

  return (
    <div className="border border-yellow-300 bg-yellow-50 rounded-lg p-4 space-y-3">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm font-medium text-yellow-800">
          🤖 AIレビュー待ち — 内容を確認し、問題なければ確定してください
        </p>
        <Button onClick={() => void confirm()} disabled={saving || !userId} size="sm">
          {saving ? '確定中…' : 'レビュー済みにする'}
        </Button>
      </div>

      {loadError && (
        <p className="text-xs text-yellow-700">変更履歴を取得できませんでした（確定は可能です）。</p>
      )}
      {entries !== null && entries.length === 0 && !loadError && (
        <p className="text-xs text-yellow-700">変更履歴が見つかりませんでした（確定は可能です）。</p>
      )}

      {entries?.map((entry, i) => (
        <div key={entry.id || i} className="text-sm text-yellow-900">
          {entry.action === 'create' ? (
            <p>
              このレコードはAIが作成しました
              {entry.at ? <span className="text-yellow-700">（{entry.at}）</span> : null}
            </p>
          ) : (
            <div>
              <p>
                AIによる変更
                {entry.at ? <span className="text-yellow-700">（{entry.at}）</span> : null}:
              </p>
              <ul className="mt-1 ml-4 list-disc space-y-0.5">
                {entry.changes.map((change) => (
                  <li key={change.field}>
                    <span className="font-medium">{label(change.field)}</span>:{' '}
                    <span className="line-through text-yellow-700">{formatValue(change.before)}</span>
                    {' → '}
                    <span className="font-medium">{formatValue(change.after)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      ))}

      {saveError && <p className="text-xs text-red-600">{saveError}</p>}
    </div>
  );
}
